import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchTestById } from "../../api/serviceApi/internTestApi";

export default function Instructions() {
  const { testId } = useParams();
  const navigate = useNavigate();

  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    fetchTestById(testId)
      .then(res => {
        setTest(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError("Invalid or expired assessment link");
        setLoading(false);
      });
  }, [testId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading assessment…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="bg-white shadow-xl rounded-lg w-full max-w-4xl overflow-hidden">

        {/* HEADER */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <h1 className="text-2xl font-bold">
            {test.role} Assessment
          </h1>
          <p className="text-sm opacity-90 mt-1">
            Powered by Botervue
          </p>
        </div>

        {/* BODY */}
        <div className="p-8 space-y-8">

          {/* META INFO */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border rounded-lg p-4 text-center">
              <p className="text-sm text-gray-500">Duration</p>
              <p className="text-xl font-semibold">
                {test.duration} minutes
              </p>
            </div>

            <div className="border rounded-lg p-4 text-center">
              <p className="text-sm text-gray-500">Questions</p>
              <p className="text-xl font-semibold">
                {test.questions.length}
              </p>
            </div>

            <div className="border rounded-lg p-4 text-center">
              <p className="text-sm text-gray-500">Attempts</p>
              <p className="text-xl font-semibold">
                One only
              </p>
            </div>
          </div>

          {/* SKILLS */}
          <div>
            <h2 className="text-lg font-semibold mb-2">
              Skills Assessed
            </h2>
            <div className="flex flex-wrap gap-2">
              {[
                "Data Structures",
                "Logical Reasoning",
                "SQL Basics",
                "Core CS Concepts"
              ].map(skill => (
                <span
                  key={skill}
                  className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>

          {/* INSTRUCTIONS */}
          <div>
            <h2 className="text-lg font-semibold mb-3">
              Instructions
            </h2>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li>• This is a timed assessment. Timer starts immediately.</li>
              <li>• You cannot pause, restart, or reattempt the test.</li>
              <li>• Switching tabs or refreshing may auto-submit your test.</li>
              <li>• All questions are mandatory.</li>
              <li>• Once submitted, answers cannot be changed.</li>
            </ul>
          </div>

          {/* BEFORE YOU BEGIN */}
          <div>
            <h2 className="text-lg font-semibold mb-3">
              Before You Begin
            </h2>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li>✓ Ensure stable internet connection</li>
              <li>✓ Close all other browser tabs</li>
              <li>✓ Allocate uninterrupted time</li>
              <li>✓ Use a laptop or desktop for best experience</li>
            </ul>
          </div>

          {/* WARNING */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
            ⚠️ <b>Important:</b> Your activity may be monitored for fairness.
            Any suspicious behavior can lead to disqualification.
          </div>

          {/* ACKNOWLEDGEMENT */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={accepted}
              onChange={e => setAccepted(e.target.checked)}
              className="mt-1"
            />
            <p className="text-sm text-gray-700">
              I understand that this assessment can be taken only once and my
              activity may be monitored during the test.
            </p>
          </div>

          {/* CTA */}
          <div className="flex justify-end pt-4">
            <button
              disabled={!accepted}
              onClick={() =>
                navigate(`/intern-test/${testId}/start`, { replace: true })
              }
              className={`px-8 py-3 rounded-md font-semibold transition
                ${
                  accepted
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
            >
              Start Assessment →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
